import { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { comnUtils } from "@/comn/utils";
import { Button, FormControl, Grid, Group, Layout, Page } from "@/comn/components";
import {
    useFetch,
    useForm,
    useGrid,
    useStore,
    useTheme,
    useResource,
    usePage,
    useToast,
    useAuth,
    useModal,
} from "@/comn/hooks";
import dayjs from "dayjs";
import {
    APIS,
    SF_LPCO_PRCSS_STTS,
    SG_BLL_PRCSS_STTS_LIST,
    SG_LPCO_HIST_LIST,
    SG_LPCO_PRCSS_STTS_LIST,
} from "./services/OsciLpcoPrcssSttsService";
import { APP_STAT_PPUP } from "../comn/APP_STAT_PPUP";
import { t } from "i18next";
import { ptlUtils } from "@/tz/go/egaz/osci/ptl/comn";

/*
 * Access Statistics List
 * !== 접속 통계 목록 ==!
 */
export const OSCI0206001Q = (): JSX.Element => {
    const toast = useToast(); // Toast Message Hook !== Toast 메세지 표시 Hook ==!
    const { pgeUid, menuId } = usePage({
        auth: ["auth_osci0003", "auth_osci0019", "auth_osci0001", "auth_osci0002"],
    });
    const { theme } = useTheme();
    const navigate = useNavigate();
    const { pgeStore, setStore } = useStore({ pgeUid: pgeUid }); // Page Store Hook !== 화면 데이터 저장 Hook ==!
    const auth = useAuth(); // Login , User Information Hook !== 로그인, 유저정보 Hook ==!
    const modal = useModal(); // Modal Window Hook !== Modal 창 Hook ==!

    useResource({
        defaultSchema: [
            { area: "comnCd", comnCd: "OSC0088" },
            { area: "comnCd", comnCd: "OSC0180" },
        ],
    });

    /*, comnCd: "OSC0180"
     * Declaration Form
     * !== 폼 정의 ==!
     */
    const form = {
        lpcoPrcssStts: useForm({
            defaultSchema: SF_LPCO_PRCSS_STTS,
            defaultValues: {
                ...pgeStore?.form,
                // [2026-10-07] 시스템 운영자(auth_osci0003)가 아니면 소속 기관 코드로만 조회
                // orgCd: auth.get("orgCd"),
                orgCd: ptlUtils.hasAuth("auth_osci0003", auth) ? "" : auth.get("orgCd"),
            },
        }),
    };

    /*
     * Declaration Grid
     * !== 그리드 정의 ==!
     */
    const grid = {
        lpcoPrcssSttsList: useGrid({
            defaultSchema: SG_LPCO_PRCSS_STTS_LIST,
            page: pgeStore?.page,
            size: pgeStore?.size,
        }),

        bllPrcssSttsList: useGrid({
            defaultSchema: SG_BLL_PRCSS_STTS_LIST,
            page: pgeStore?.page,
            size: pgeStore?.size,
        }),

        lpcoHistLst: useGrid({
            defaultSchema: SG_LPCO_HIST_LIST,
            page: pgeStore?.page,
            size: pgeStore?.size,
        }),
    };

    /*
     * Declaration Fetch
     * !== Fetch 정의 ==!
     */
    const fetch = {
        getLpcoPrcssStssList: useFetch({
            api: () => APIS.getLpcoPrcssStssList(form.lpcoPrcssStts.getValues()),
            enabled: comnUtils.isEmpty(form.lpcoPrcssStts.errors) && form.lpcoPrcssStts.isSubmitted,
            onSuccess: (data) => {
                const lpcoPrcssSttsLst = data.prcssSttsDto.content.lpcoPrcssSttsLst || [];
                grid.lpcoPrcssSttsList.setData(comnUtils.getGridData(lpcoPrcssSttsLst));

                const bllPrcssSttsLst = data.prcssSttsDto.content.bllPrcssSttsLst || [];
                grid.bllPrcssSttsList.setData(comnUtils.getGridData(bllPrcssSttsLst));

                const lpcoHistLst = data.prcssSttsDto.content.lpcoHistLst || [];
                grid.lpcoHistLst.setData(comnUtils.getGridData(lpcoHistLst));
            },
            // [2026-10-09] 고정 토스트 대신 서버 오류 메시지를 모달로 표시(없으면 기존 메시지)
            // onError: () => toast.showToast({ type: "error", content: "msg.osc.00043" }), // Failed to select. !== 조회가 실패하였습니다. ==!
            onError: (error) => {
                modal.openModal({
                    content: error?.response?.data?.errorMessage || "msg.osc.00043", // Failed to select. !== 조회가 실패하였습니다. ==!
                });
            },
        }),
    };

    const render = {
        lpcoPrcssSttsList: {
            cell: {
                orgCd: (props: any) => {
                    const { binding, rowValues, value } = props;
                    if (rowValues.appNm === "TANSAD") {
                        return (
                            <Layout direction="col" gap={1}>
                                {`${rowValues.orgCd}`}
                            </Layout>
                        );
                    }
                },
                appNo: (props: any) => {
                    const { value } = props;
                    // LPCO already submitted as a TANSAD attachment: the server sends "N/A", shown as "Attached File".
                    // !== 첨부서류로 이미 등록된 LPCO. 서버가 N/A 로 보내고 신청번호 칸에 "Attached File" 로 표시한다. (결함관리대장 353번) ==!
                    if (value === "N/A") {
                        return (
                            <Layout direction="col" gap={1} valign="center">
                                {t("L_ATCD_FILE")}
                            </Layout>
                        );
                    }
                },
                appNm: (props: any) => {
                    const { value } = props;
                    // The server appends " (Non-target LPCO)" to the form name; show "(Non-target LPCO)" in red so it stands out.
                    // !== 서버가 서식명 뒤에 " (Non-target LPCO)" 를 붙여 보낸다. 눈에 띄도록 "(Non-target LPCO)" 만 빨간색으로 표시한다. ==!
                    const nonTrgt = "(Non-target LPCO)";
                    if (typeof value === "string" && value.endsWith(nonTrgt)) {
                        // No valign: in a column Layout it centres horizontally, while the other rows are left-aligned.
                        // !== valign 을 주지 않는다. 세로(col) Layout 에서는 가로 가운데 정렬이 되어 다른 행(왼쪽 정렬)과 어긋난다. ==!
                        return (
                            <Layout direction="col" gap={1}>
                                <span>
                                    {value.slice(0, -nonTrgt.length)}
                                    <span className="text-error">{nonTrgt}</span>
                                </span>
                            </Layout>
                        );
                    }
                },
                appStatCd: (props: any) => {
                    const { binding, rowValues, value } = props;
                    if (rowValues.appNm === "TANSAD") {
                        return (
                            <Layout direction="col" gap={1} valign="center">
                                <FormControl
                                    type="code"
                                    area="comnCd"
                                    comnCd="OSC0018"
                                    value={value}
                                    edit={false}
                                    viewType="label"
                                />
                            </Layout>
                        );
                    } else if (rowValues.appStatCd !== null && rowValues.appStatCd !== "") {
                        return (
                            <Layout direction="col" gap={1} valign="center">
                                <Link
                                    to={""}
                                    onClick={() => {
                                        modal.openModal({
                                            content: (
                                                <APP_STAT_PPUP
                                                    paramData={{
                                                        appId: rowValues.appId,
                                                        appCdNo: rowValues.appCdNo,
                                                        appStatCd: rowValues.appStatCd,
                                                        lpcoAplyTpCd: rowValues.amdTpCd,
                                                    }}
                                                    callbackFn={(data: any) => {
                                                        modal.closeModal();
                                                        window.location.reload();
                                                    }}
                                                />
                                            ),
                                            size: "sm",
                                            height: 200,
                                        });
                                    }}
                                >
                                    {`${rowValues.appStatNm}`}
                                </Link>
                            </Layout>
                        );
                    }
                },
            },
        },
        bllPrcssSttsList: {
            cell: {
                bllTpCd: (props: any) => {
                    const { binding, rowValues, value } = props;
                    if (rowValues.bllTpCd === "L") {
                        if (rowValues.grpBllMgmtId !== rowValues.bllMgmtId) {
                            return (
                                <Layout direction="col" gap={1}>
                                    {t("T_BLL_DTL_1")}
                                </Layout>
                            );
                        }
                    }
                },
            },
        },
        lpcoHistLst: {
            cell: {
                lpcoNm: (props: any) => {
                    const { binding, rowValues, value } = props;
                    if (rowValues.bsopTpCd === "T") {
                        return (
                            <Layout direction="col" gap={1}>
                                {t("L_TANSAD")}
                            </Layout>
                        );
                    } else if (rowValues.bsopTpCd === "B") {
                        return (
                            <Layout direction="col" gap={1}>
                                {t("L_BLL")}
                            </Layout>
                        );
                    } else if (rowValues.bsopTpCd === "I") {
                        return (
                            <Layout direction="col" gap={1}>
                                {t("L_JNT_INSC")}
                            </Layout>
                        );
                    }
                },
                lpcoNo: (props: any) => {
                    const { binding, rowValues, value } = props;
                    if (rowValues.bsopTpCd === "T") {
                        return (
                            <Layout direction="col" gap={1}>
                                {rowValues.tansadNo}
                            </Layout>
                        );
                    } else if (rowValues.bsopTpCd === "I") {
                        return (
                            <Layout direction="col" gap={1}>
                                {rowValues.tansadNo}
                            </Layout>
                        );
                    }
                },
            },
        },
    };

    /*
     * Declaration Event Handler
     * !== 이벤트 핸들러 정의 ==!
     */
    const handler = {
        // Click Search Button !== 검색버튼 ==!
        onSrchBtnClick: () => {
            form.lpcoPrcssStts.handleSubmit(
                () => {
                    fetch.getLpcoPrcssStssList.fetch();
                },
                () => {
                    toast.showToast({ type: "warning", content: "msg.osc.00003" }); // Data is invalid, !== 데이터가 유효하지않습니다. ==!
                },
            )();
        },
    };

    /*
     * Declaration Page Init Function
     * !== 화면 초기화 함수 선언  ==!
     */
    useEffect(() => {}, [theme]);

    return (
        <Page>
            <Group>
                <Group.Body>
                    <Group.Section>
                        <Group.Row>
                            <Group.Control {...form.lpcoPrcssStts.schema.srchUcrNo} />
                            <Group.Control {...form.lpcoPrcssStts.schema.srchAppNo} />
                        </Group.Row>
                    </Group.Section>
                    <Layout>
                        <Layout.Left>
                            <Button
                                role={"reset"}
                                onClick={() => {
                                    form.lpcoPrcssStts.reset();
                                    form.lpcoPrcssStts.setValues({
                                        acssDtStrt: comnUtils.getDate({ m: -1 }),
                                        acssDtEnd: comnUtils.getDate(),
                                        bsopClsfCd: "",
                                        usrId: "",
                                    });
                                }}
                            />
                        </Layout.Left>
                        <Layout.Right>
                            <Button role={"search"} onClick={handler.onSrchBtnClick} />
                        </Layout.Right>
                    </Layout>
                </Group.Body>
            </Group>
            <Group>
                <Group.Body>
                    <Group.Title title={"T_LPCO"} titleSize={1} />
                    {/* Guide for the Bill Setting / Bill Issuance columns !== 고지 설정·고지 발행 컬럼 안내 ==! */}
                    <ul className="list-disc pl-5 text-sm text-darkgray">
                        <li>{t("msg.osc.00329")}</li>
                        <li>{t("msg.osc.00330")}</li>
                    </ul>
                    <Group.Section>
                        <Grid {...grid.lpcoPrcssSttsList.grid} render={render.lpcoPrcssSttsList} />
                    </Group.Section>
                    <Group.Title title={"L_BLL"} titleSize={1} />
                    <Group.Section>
                        <Grid {...grid.bllPrcssSttsList.grid} render={render.bllPrcssSttsList} />
                    </Group.Section>
                    <Group.Title title={"B_PRCSS_HIST"} titleSize={1} />
                    <Group.Section>
                        <Grid {...grid.lpcoHistLst.grid} render={render.lpcoHistLst} />
                    </Group.Section>
                </Group.Body>
            </Group>
        </Page>
    );
};
